/**
 * Place des Arts — comportements de PRÉSENTATION des filtres catalogue.
 *
 * Le moteur de filtrage reste 100 % natif (facets.js + Section Rendering API).
 * Ce module n'ajoute que :
 *  1. l'ouverture/fermeture de la sidebar verticale (pill « Tous les filtres »,
 *     croix du header) — état persisté en localStorage et porté par une classe
 *     sur <html> (jamais re-rendu par le morph de section) ;
 *  2. les pills d'accès rapide (ouvrent la sidebar + déplient/scrollent vers le groupe) ;
 *  3. la recherche locale dans les groupes à longues listes (<pda-facet-search>) ;
 *  4. le double-slider Prix (<pda-price-slider>), enrichissement progressif
 *     par-dessus les inputs natifs filter.v.price.gte / lte.
 *
 * Tous les listeners de boutons sont délégués au document : ils survivent
 * au re-render des sections sans re-binding.
 */

/* ⛔ MÊMES CLÉS QUE `pda-catalog.js`, PARCE QUE C'EST LE MÊME BOUTON. Les facettes à
 * haute cardinalité (Artiste, Éditeur) sont rendues par le moteur, qui écrit déjà
 * `pda.filtres.voir_plus` / `voir_moins` dans `.pda-facet-more__label` ; toutes les
 * autres facettes du MÊME panneau passent par le code ci-dessous. Tant que celui-ci
 * gardait ses littéraux français, la première traduction posée aurait donné deux
 * langues sur le même bouton selon la facette ouverte.
 * `trad` et non `t` : même raison que dans `pda-catalog.js` (collision de noms), et
 * même dégradation — on peint la clé plutôt que de tuer le module, donc la sidebar.
 * Ce fichier est chargé en `type="module"`, donc APRÈS le runtime. */
const trad = (cle, params) =>
  (window.PDA && typeof window.PDA.t === 'function') ? window.PDA.t(cle, params) : cle;

const STORAGE_KEY = 'pda:catalog-filters-open';
const CLOSED_CLASS = 'pda-filters-closed';
const root = document.documentElement;

const isOpen = () => !root.classList.contains(CLOSED_CLASS);

// ── Drawer mobile (B6) : backdrop + scroll-lock + focus ──────────────────────
// Sur mobile la sidebar passe en `position: fixed` (CSS). On détecte ce « mode
// drawer » par la position calculée → backdrop + verrou de scroll + focus, sans
// dépendre du breakpoint (collections 750 / artistes 990 diffèrent).
let backdrop = null;
let lastToggleFocus = null;

function sidebarEl() {
  return document.querySelector(
    '.pda-cat__sidebar, .collection-wrapper .facets-block-wrapper--vertical:not(#filters-drawer)'
  );
}
function isDrawerMode() {
  const el = sidebarEl();
  return el ? getComputedStyle(el).position === 'fixed' : false;
}
function ensureBackdrop() {
  if (backdrop || !document.body) return;
  // Pas de filtres PDA sur cette page (ex. S&D natif sans chrome) → pas de backdrop.
  if (!document.querySelector('[data-pda-filters-toggle]')) return;
  backdrop = document.createElement('div');
  backdrop.className = 'pda-facets-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  backdrop.addEventListener('click', () => setOpen(false));
  document.body.appendChild(backdrop);
}
function syncDrawer(focusOnOpen) {
  ensureBackdrop();
  const el = sidebarEl();
  const drawer = isOpen() && isDrawerMode();
  root.classList.toggle('pda-facets-lock', drawer);
  // Sémantique modale du drawer (a11y) — aligné sur le panneau de recherche 4B.
  if (el) {
    if (drawer) {
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      /* Repli localisé : pda.filtres.titre est js:true ; PDA absent ⇒ FR (visible au differ). */
      if (!el.getAttribute('aria-label')) {
        var pdaT = window.PDA && typeof window.PDA.t === 'function' ? window.PDA.t('pda.filtres.titre') : 'Filtres';
        el.setAttribute('aria-label', pdaT);
      }
    } else {
      el.removeAttribute('role');
      el.removeAttribute('aria-modal');
    }
  }
  if (drawer && focusOnOpen && el) {
    const close = el.querySelector('.pda-facets-header__close, [data-pda-filters-toggle]');
    if (close && typeof close.focus === 'function') close.focus();
  }
}

/**
 * Ouvre/ferme la sidebar de filtres.
 * @param {boolean} open - L'état cible
 * @param {boolean} [persist] - Persister l'état en localStorage
 */
function setOpen(open, persist = true) {
  // Mémorise le déclencheur (pour restituer le focus à la fermeture du drawer).
  if (open && persist && !isOpen()) lastToggleFocus = document.activeElement;

  root.classList.toggle(CLOSED_CLASS, !open);

  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, open ? 'open' : 'closed');
    } catch {
      /* stockage indisponible : état non persisté */
    }
  }

  for (const button of document.querySelectorAll('[data-pda-filters-toggle][aria-controls]')) {
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // Drawer mobile : verrou + backdrop (focus uniquement sur action utilisateur).
  syncDrawer(persist);
  if (!open && persist) {
    // Restaure le focus sur le déclencheur ; repli sur la pill « Tous les filtres »
    // (Safari/Firefox macOS ne focalisent pas un <button> au clic → activeElement = body).
    const target =
      lastToggleFocus && lastToggleFocus.isConnected && lastToggleFocus !== document.body
        ? lastToggleFocus
        : document.querySelector('.pda-facets-bar__all, [data-pda-filters-toggle]');
    if (target && typeof target.focus === 'function') target.focus();
    lastToggleFocus = null;
  }
}

// État initial. Sur les pages moteur (blocks/pda-filters.liquid), un script inline
// anti-FOUC a déjà posé la classe (FERMÉ par défaut, état du design). Ailleurs
// (sidebar native S&D), pas de classe → défaut ouvert. On honore d'abord une
// préférence utilisateur sauvegardée, sinon on respecte l'état déjà en place.
try {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'open') setOpen(true, false);
  else if (saved === 'closed') setOpen(false, false);
  else setOpen(isOpen(), false);
} catch {
  setOpen(isOpen(), false);
}

// ── Toggle densité de grille MOBILE (<768) — maître Figma 2170:83175 (Sam 16/07) ──
// Deux boutons (snippet pda-grid-toggle) : 2 colonnes (défaut) / 1 colonne. L'état
// vit sur <html> (classe pda-grid-single, posée anti-FOUC par le script inline des
// catalogues) + localStorage. CSS : §1 grilles (1 col) + §16.b (icônes 30/90).
const GRID_SINGLE_KEY = 'pda:grid-mobile-single';
const GRID_SINGLE_CLASS = 'pda-grid-single';

function syncGridToggle() {
  const single = root.classList.contains(GRID_SINGLE_CLASS);
  for (const btn of document.querySelectorAll('[data-pda-grid-cols]')) {
    const active = (btn.dataset.pdaGridCols === '1') === single;
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
}

function setGridSingle(single) {
  root.classList.toggle(GRID_SINGLE_CLASS, single);
  try {
    localStorage.setItem(GRID_SINGLE_KEY, single ? '1' : '2');
  } catch {
    /* stockage indisponible : état non persisté */
  }
  syncGridToggle();
}

// Resync l'aria-pressed au chargement (l'anti-FOUC inline n'a posé que la classe).
syncGridToggle();

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // Un clic sur une checkbox de facette est ré-hydraté (innerHTML) → le nœud est
  // détaché avant de remonter ici ; ne JAMAIS fermer un dropdown dans ce cas
  // (sinon impossible de cocher plusieurs valeurs d'affilée dans un quick-pill).
  if (!target.isConnected || target.closest('[data-pda-facet-body]')) return;

  // Ferme les dropdowns de la barre (Trier + quick-pills) au clic à l'extérieur.
  // (L'attribut name="pda-bar-dd" les rend déjà mutuellement exclusifs à l'ouverture.)
  const insideDropdown = target.closest('.pda-dropdown');
  for (const dd of document.querySelectorAll('details.pda-dropdown[open]')) {
    if (dd !== insideDropdown) dd.open = false;
  }

  // Ouvre/ferme la sidebar (« Tous les filtres », croix du header).
  // Les quick-pills n'ouvrent PLUS la sidebar : ce sont des dropdowns autonomes.
  if (target.closest('[data-pda-filters-toggle]')) setOpen(!isOpen());

  // Toggle densité de grille mobile (1/2 colonnes).
  const gridBtn = target.closest('[data-pda-grid-cols]');
  if (gridBtn instanceof HTMLElement) setGridSingle(gridBtn.dataset.pdaGridCols === '1');
});

// Échap ferme tout dropdown ouvert de la barre ; sinon, ferme le drawer mobile.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  let closed = false;
  for (const dd of document.querySelectorAll('details.pda-dropdown[open]')) {
    dd.open = false;
    closed = true;
  }
  if (!closed && isOpen() && isDrawerMode()) setOpen(false);
});

// Resize : garde le verrou de scroll cohérent au franchissement du breakpoint drawer.
let resizeRaf = 0;
window.addEventListener(
  'resize',
  () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      syncDrawer(false);
    });
  },
  { passive: true }
);

// Piège de focus dans le drawer mobile (dialog plein écran).
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab' || !isOpen() || !isDrawerMode()) return;
  const el = sidebarEl();
  if (!el) return;
  const focusables = Array.from(
    el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
  ).filter((n) => !n.hidden && n.offsetParent !== null);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  // Focus éjecté hors du drawer (ex. re-render innerHTML d'une facette cochée) → on le ramène.
  if (!el.contains(document.activeElement)) {
    event.preventDefault();
    first.focus();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

/**
 * Normalise une chaîne pour la recherche locale (minuscules, sans accents).
 * @param {string} value
 * @returns {string}
 */
const normalize = (value) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Recherche locale dans la liste de valeurs d'un groupe de filtres.
 * Pur affichage : ne touche ni aux inputs ni au form.
 */
class PdaFacetSearch extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLInputElement | null} */
    this.input = this.querySelector('input');
    this.input?.addEventListener('input', () => this.#apply());
  }

  #apply() {
    const host = this.closest('facet-inputs-component');
    if (!host || !this.input) return;

    const query = normalize(this.input.value.trim());
    host.classList.toggle('pda-searching', query.length > 0);

    for (const item of host.querySelectorAll('.facets__inputs-list-item')) {
      const label = item.querySelector('.checkbox__label-text, label');
      const text = normalize((label ?? item).textContent ?? '');
      item.classList.toggle('pda-search-hidden', query.length > 0 && !text.includes(query));
    }
  }
}

if (!customElements.get('pda-facet-search')) {
  customElements.define('pda-facet-search', PdaFacetSearch);
}

/* ──────────────────────────────────────────────────────────────────────────
   « Voir plus » + recherche-dans-facette pilotés par le COMPTEUR (> 5 options)
   ----------------------------------------------------------------------------
   Décision Sam (2026-06-22, seuil recalé à 5 le 22/07 quater — parité app
   TRUNCATE=5) : toute facette à liste de PLUS DE 5 entrées reçoit (a) le champ
   de recherche-dans-facette et (b) un repli à 5 éléments visibles + un bouton
   « Voir plus (N) / Voir moins ». Présentation PURE pour UN corps de facette
   ([data-pda-facet-body], sidebar OU quick-pill).

   Appelée par les DEUX catalogues après chaque (ré)hydratation d'un corps :
   pda-catalog.js (œuvres, facettes faible cardinalité) et pda-artists-catalog.js.
   Les facettes HAUTE CARDINALITÉ (Artiste/Éditeur) du catalogue œuvres ne passent
   PAS par ici : pda-catalog.js gère leur top-N + « Voir plus » via searchValues
   (recherche déjà visible d'emblée).

   Idempotente. L'état replié/déplié vit sur le CONTRÔLE (data-pda-collapsed),
   donc survit au remplacement d'innerHTML du corps (qui, lui, est dans le wrapper).
   ────────────────────────────────────────────────────────────────────────── */
const FACET_OVERFLOW_LIMIT = 5;

/**
 * @param {Element | null} body - le <ul data-pda-facet-body>
 */
function applyFacetOverflow(body) {
  if (!body) return;
  const control = body.closest('.facets__panel-content, .pda-dropdown__menu--facet');
  if (!control) return;

  // Dropdown de quick-pill (directive Sam 22/07 ter, modèle app) : SCROLL INTÉGRÉ —
  // pas de repli ni de « Voir plus » (toutes les valeurs listées, le wrapper scrolle,
  // CSS §16.j). La révélation de la recherche (> FACET_OVERFLOW_LIMIT options) est conservée.
  const isDropdown = control.matches('.pda-dropdown__menu--facet');

  const search = control.querySelector('.pda-facet-search');
  const input = search ? search.querySelector('input') : null;
  const searching = !!(input && input.value.trim());
  const collapsed = control.getAttribute('data-pda-collapsed') !== 'false';

  // Deux compteurs distincts :
  //  - `available` = options contextuellement disponibles (compteur > 0), recherche
  //    IGNORÉE → stable pendant la frappe (le champ ne disparaît pas en cours de
  //    recherche). Pilote la VISIBILITÉ de la recherche ET du bouton (couplés).
  //  - `shown` = options réellement affichées (hors recherche-masquées) → sert au
  //    repli aux 5 premières (classe .pda-facet-overflow au-delà).
  let available = 0;
  let shown = 0;
  for (const li of body.querySelectorAll('.facets__inputs-list-item')) {
    if (li.hasAttribute('hidden')) { li.classList.remove('pda-facet-overflow'); continue; }
    available += 1;
    if (li.classList.contains('pda-search-hidden')) { li.classList.remove('pda-facet-overflow'); continue; }
    shown += 1;
    li.classList.toggle('pda-facet-overflow', !isDropdown && collapsed && !searching && shown > FACET_OVERFLOW_LIMIT);
  }

  // Recherche-dans-facette : visible dès > 5 options disponibles.
  if (search) search.hidden = available <= FACET_OVERFLOW_LIMIT;

  // Bouton « Voir plus / Voir moins » : > 5 options dispo et hors recherche (la
  // recherche montre déjà toutes les correspondances). Couplé à la recherche.
  const more = control.querySelector('.pda-facet-more');
  if (more) {
    const need = !isDropdown && !searching && available > FACET_OVERFLOW_LIMIT;
    more.hidden = !need;
    if (need) {
      const label = more.querySelector('.pda-facet-more__label') || more;
      /* `voir_plus_compte` (avec le nombre) et non `voir_plus` : ici on annonce combien
       * d'options restent cachées. Le moteur, lui, ne peut pas les compter sur une facette
       * haute cardinalité et utilise `voir_plus`. Deux clés, deux phrases, une seule
       * source — le nombre est passé BRUT, `PDA.t` le met au format de la locale. */
      label.textContent = collapsed
        ? trad('pda.filtres.voir_plus_compte', { count: available - FACET_OVERFLOW_LIMIT })
        : trad('pda.filtres.voir_moins');
      more.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      more.classList.toggle('pda-facet-more--less', !collapsed);
    }
  }
}

/**
 * Bascule replié/déplié d'un contrôle puis ré-applique. Les catalogues l'appellent
 * depuis leur propre écouteur de clic délégué (sauf facettes haute cardinalité).
 * @param {Element} moreButton
 */
function toggleFacetOverflow(moreButton) {
  const control = moreButton.closest('.facets__panel-content, .pda-dropdown__menu--facet');
  if (!control) return;
  const collapsed = control.getAttribute('data-pda-collapsed') !== 'false';
  control.setAttribute('data-pda-collapsed', collapsed ? 'false' : 'true');
  applyFacetOverflow(control.querySelector('[data-pda-facet-body]'));
}

window.PdaFacets = window.PdaFacets || {};
window.PdaFacets.applyFacetOverflow = applyFacetOverflow;
window.PdaFacets.toggleFacetOverflow = toggleFacetOverflow;
window.PdaFacets.OVERFLOW_LIMIT = FACET_OVERFLOW_LIMIT;
// Filet d'ordre de chargement : signale aux catalogues chargés en classic `defer`
// (ex. pda-artists-catalog.js) que le helper est prêt, au cas où ils auraient
// hydraté des facettes AVANT l'exécution de ce module.
try { window.dispatchEvent(new CustomEvent('pda:facets-ready')); } catch (e) { /* no-op */ }

/**
 * Double-slider de prix. Valeurs en unités mineures (centimes), affichage en
 * unités entières. Écrit dans les inputs natifs min/max du price-facet-component
 * puis leur fait émettre `change` → le natif déclenche le re-render.
 */
class PdaPriceSlider extends HTMLElement {
  static observedAttributes = ['data-current-min', 'data-current-max'];

  #dragging = false;

  connectedCallback() {
    /** @type {HTMLInputElement | null} */
    this.minRange = this.querySelector('[data-pda-slider-min]');
    /** @type {HTMLInputElement | null} */
    this.maxRange = this.querySelector('[data-pda-slider-max]');
    this.facet = this.closest('price-facet-component');

    this.minRange?.addEventListener('input', () => this.#onInput('min'));
    this.maxRange?.addEventListener('input', () => this.#onInput('max'));

    for (const range of [this.minRange, this.maxRange]) {
      if (!range) continue;
      range.addEventListener('pointerdown', () => (this.#dragging = true));
      range.addEventListener('pointerup', () => (this.#dragging = false));
      range.addEventListener('change', () => this.#commit());
    }

    // Resync quand l'utilisateur tape dans les inputs texte natifs.
    this.facet?.addEventListener('change', (event) => {
      if (event.target instanceof Element && !this.contains(event.target)) this.#syncFromInputs();
    });

    this.#syncFromInputs();
  }

  attributeChangedCallback() {
    // Le morph de section met à jour les data-attributes serveur : on resynchronise.
    if (this.isConnected && !this.#dragging) this.#syncFromInputs();
  }

  get #rangeMax() {
    return Number(this.minRange?.max ?? 0) || 0;
  }

  /** @returns {{ minInput: HTMLInputElement | null, maxInput: HTMLInputElement | null }} */
  get #textInputs() {
    return {
      minInput: this.facet?.querySelector('[ref="minInput"]') ?? null,
      maxInput: this.facet?.querySelector('[ref="maxInput"]') ?? null,
    };
  }

  /**
   * Parse la valeur d'un input texte natif (« 1 250 », « 1250,50 ») en centimes.
   * @param {HTMLInputElement | null} input
   * @param {number} fallback
   * @returns {number}
   */
  #parseInput(input, fallback) {
    if (!input || input.value.trim() === '') return fallback;
    const parsed = (window.PDA && PDA.parseNombre) ? PDA.parseNombre(input.value) : parseFloat(String(input.value).replace(/[\s'\u2019]/g, '').replace(',', '.'));
    if (Number.isNaN(parsed)) return fallback;
    /* Le champ porte la DEVISE du marché ; le curseur travaille en centimes d'EURO.
     * On repasse en euros par une conversion INVERSIBLE — `versEuros`, pas `calage` —
     * exactement comme `PdaRangeSlider.#parseInput` utilise `PDA.versCm`.
     * ⛔ SURTOUT PAS DE CALAGE ICI : cette valeur ne part PAS vers le moteur, elle place
     * une POIGNÉE. Le calage est un accrochage sur les prix réels du catalogue ; appliqué
     * à un curseur il l'empêche de bouger. Mesuré le 29/08 : 4 flèches clavier sans effet
     * en JPY (aucun prix réel entre 4 500 et 5 000 €, la poignée revenait à son point de
     * départ), et un décalage de 10,66 px entre la poignée et la borne annoncée.
     * ⛔ Le CALAGE, lui, vit à l'ÉCRITURE — pda-catalog.js, à l'entrée de setRange :
     * c'est là que naît la borne qui sélectionne des œuvres, et là seulement. */
    const euros = (window.PDA && PDA.versEuros) ? PDA.versEuros(parsed) : parsed;
    return Math.round(euros * 100);
  }

  /**
   * @param {'min' | 'max'} which - La poignée déplacée
   */
  #onInput(which) {
    if (!this.minRange || !this.maxRange) return;

    let min = Number(this.minRange.value);
    let max = Number(this.maxRange.value);

    /* ⛔ COTE SAM 29/08 : LE CURSEUR AVANCE DE 50 EN 50 EUROS. Son domaine est en
     * centimes d'EURO (step 5000) et le reste dans les 63 devises : il n'accroche sur
     * rien et ne se convertit pas. C'est le CHAMP qui parle la devise, pas la piste.
     * ⇒ la valeur du curseur est DÉJÀ une borne euro : elle ne doit surtout pas être
     * recalée en repassant par le champ (c'est ce cycle qui figeait la poignée). */
    /* ⛔ On ne publie QUE la borne de la poignée déplacée. Publier les deux faisait
     * repasser l'autre par la value du range, re-snappée sur la grille de 50 € : une
     * borne tapée hors grille était réalignée (485 → 500), et si elle tombait au-dessus
     * du plafond arrondi elle était purement SUPPRIMÉE — mesuré sur petits-prix, « 95 »
     * tapé (la valeur même du placeholder) disparaissait au premier geste sur l'AUTRE
     * poignée. Une poignée ne parle que d'elle-même. */
    // Empêche le croisement des poignées.
    if (min > max) {
      if (which === 'min') {
        min = max;
        this.minRange.value = String(min);
      } else {
        max = min;
        this.maxRange.value = String(max);
      }
    }

    /* ⛔ APRÈS le clamp, jamais avant : publier la borne en amont contournait la garde
     * anti-croisement et émettait « prix_min=10000 & prix_max=1000 » — le filtre disait
     * l'inverse du curseur, qui s'était pourtant borné correctement. Avant ce lot la
     * garde tenait par construction : la borne venait du champ, écrit ici même. */
    if (which === 'min') this.dataset.pdaEurosMin = String(min);
    else this.dataset.pdaEurosMax = String(max);

    const { minInput, maxInput } = this.#textInputs;
    /* Zone MACHINE (lot 0) ET devise du marché (lot 2) : prixAffiche rend TOUJOURS un
     * entier en unités majeures — aucun séparateur à gérer, le contrat du lot 0 survit. */
    const aff = (c) => String(window.PDA && PDA.prixAffiche ? PDA.prixAffiche(c / 100) : Math.round(c / 100));
    if (minInput) minInput.value = min <= 0 ? '' : aff(min);
    if (maxInput) maxInput.value = max >= this.#rangeMax ? '' : aff(max);

    this.#paint(min, max);
  }

  /** Au relâchement d'une poignée : déclenche la mécanique native. */
  #commit() {
    this.#dragging = false;
    const { minInput, maxInput } = this.#textInputs;
    (minInput ?? maxInput)?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncFromInputs() {
    if (!this.minRange || !this.maxRange) return;

    const { minInput, maxInput } = this.#textInputs;
    const min = Math.max(0, Math.min(this.#parseInput(minInput, 0), this.#rangeMax));
    const max = Math.max(min, Math.min(this.#parseInput(maxInput, this.#rangeMax), this.#rangeMax));

    this.minRange.value = String(min);
    this.maxRange.value = String(max);
    this.#paint(min, max);
  }

  /**
   * Positionne le segment actif rose entre les deux poignées.
   * @param {number} min - Valeur min (centimes)
   * @param {number} max - Valeur max (centimes)
   */
  #paint(min, max) {
    const rangeMax = this.#rangeMax || 1;
    this.style.setProperty('--pda-fill-start', `${(min / rangeMax) * 100}%`);
    this.style.setProperty('--pda-fill-end', `${(max / rangeMax) * 100}%`);
  }
}

if (!customElements.get('pda-price-slider')) {
  customElements.define('pda-price-slider', PdaPriceSlider);
}

/**
 * Double-slider GÉNÉRIQUE (Dimension largeur/hauteur, Année). Même structure
 * visuelle que le slider Prix mais SANS conversion d'unité : la valeur du slider
 * = la valeur du filtre (cm, année). Les bornes sont data-driven via l'attribut
 * `data-bounds="min,max"` (posé par pda-catalog.js depuis l'index). Les inputs
 * texte liés sont désignés par `data-min-input` / `data-max-input` (IDs).
 *
 * Écrit '' dans un input quand la poignée est à l'extrême (= pas de borne), puis
 * fait émettre `change` sur l'input min au relâchement → pda-catalog filtre.
 */
class PdaRangeSlider extends HTMLElement {
  static observedAttributes = ['data-bounds'];

  #dragging = false;

  connectedCallback() {
    /** @type {HTMLInputElement | null} */
    this.minRange = this.querySelector('[data-pda-slider-min]');
    /** @type {HTMLInputElement | null} */
    this.maxRange = this.querySelector('[data-pda-slider-max]');
    this.minInput = document.getElementById(this.dataset.minInput ?? '');
    this.maxInput = document.getElementById(this.dataset.maxInput ?? '');
    if (!this.minRange || !this.maxRange) return;

    this.minRange.addEventListener('input', () => this.#onInput('min'));
    this.maxRange.addEventListener('input', () => this.#onInput('max'));
    for (const range of [this.minRange, this.maxRange]) {
      range.addEventListener('pointerdown', () => (this.#dragging = true));
      range.addEventListener('pointerup', () => (this.#dragging = false));
      range.addEventListener('change', () => this.#commit());
    }
    // Resync quand l'utilisateur tape dans les inputs texte (ou restauration URL).
    for (const input of [this.minInput, this.maxInput]) {
      input?.addEventListener('change', () => {
        if (!this.#dragging) this.#syncFromInputs();
      });
    }
    this.#syncFromInputs();
  }

  attributeChangedCallback(name) {
    if (name === 'data-bounds' && this.isConnected) this.#applyBounds();
  }

  #applyBounds() {
    if (!this.minRange || !this.maxRange) return;
    const [lo, hi] = (this.dataset.bounds ?? '').split(',').map(Number);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return;
    for (const range of [this.minRange, this.maxRange]) {
      range.min = String(lo);
      range.max = String(hi);
    }
    if (!this.#dragging) this.#syncFromInputs();
  }

  get #lo() {
    return Number(this.minRange?.min ?? 0) || 0;
  }
  get #hi() {
    return Number(this.minRange?.max ?? 100) || 100;
  }

  /** @param {HTMLInputElement | null} input @param {number} fallback */
  /* ⛔ CE CURSEUR SERT AUSSI L'ANNÉE, QUI N'EST PAS UNE LONGUEUR. La clé de facette
   * vient du groupe parent (`data-pda-dim="w"` / `"hg"`) ; le groupe Année n'en a pas,
   * donc `undefined`, et `PDA.versCm` refuse de convertir ce qu'il ne reconnaît pas.
   * Sans ce garde-fou, basculer en pouces diviserait les ANNÉES par 2,54. */
  #cle() {
    return this.closest('[data-pda-dim]')?.getAttribute('data-pda-dim') ?? undefined;
  }

  #parseInput(input, fallback) {
    if (!input || input.value.trim() === '') return fallback;
    const parsed = (window.PDA && PDA.parseNombre) ? PDA.parseNombre(input.value) : parseFloat(String(input.value).replace(/[\s'\u2019]/g, '').replace(',', '.'));
    if (Number.isNaN(parsed)) return fallback;
    /* Le champ porte l'unité AFFICHÉE ; le curseur, lui, travaille dans l'unité de
     * l'index (centimètres). On repasse donc en centimètres à la lecture. */
    return (window.PDA && PDA.versCm) ? PDA.versCm(parsed, this.#cle()) : parsed;
  }

  /** @param {'min' | 'max'} which */
  #onInput(which) {
    if (!this.minRange || !this.maxRange) return;
    let min = Number(this.minRange.value);
    let max = Number(this.maxRange.value);
    if (min > max) {
      if (which === 'min') {
        min = max;
        this.minRange.value = String(min);
      } else {
        max = min;
        this.maxRange.value = String(max);
      }
    }
    /* Le curseur pense en centimètres (ses bornes viennent de l'index) ; le champ
     * texte montre l'unité active. Un `Math.round` sur des pouces perdrait trop :
     * on garde une décimale, comme les dimensions des cartes. */
    const aff = (v) => {
      const x = (window.PDA && PDA.versAffichage) ? PDA.versAffichage(v, this.#cle()) : v;
      const arrondi = Math.round(x * 10) / 10;
      /* ⛔ PAS de formatage localisé ici : cette valeur est RELUE par #parseInput
       * (plus bas) et par onRangeInput (pda-catalog.js), qui lisent « , » et « . »
       * comme des séparateurs DÉCIMAUX. Un champ de fourchette est une zone MACHINE.
       * Mesuré le 29/08 : PDA.nombre peignait « 1,950 » en en et « 1.950 » en de,
       * relus 1.95, repeints « 2 » — la poignée Année sautait à 2 à chaque glissement.
       * Cote Sam 29/08 : le champ reste en chiffres nus dans les 5 locales ; le format
       * lisible vit sur le placeholder et sur le chip, qui ne sont jamais relus.
       * ⚠️ nombreNu et NON String() : la cote porte sur les MILLIERS. En pouces cette
       * valeur a une décimale, et String() poserait « 15.7 » à côté d'un chip « 15,7 in ». */
      return (window.PDA && PDA.nombreNu) ? PDA.nombreNu(arrondi) : String(arrondi);
    };
    if (this.minInput) this.minInput.value = min <= this.#lo ? '' : aff(min);
    if (this.maxInput) this.maxInput.value = max >= this.#hi ? '' : aff(max);
    this.#paint(min, max);
  }

  #commit() {
    this.#dragging = false;
    (this.minInput ?? this.maxInput)?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncFromInputs() {
    if (!this.minRange || !this.maxRange) return;
    const min = Math.max(this.#lo, Math.min(this.#parseInput(this.minInput, this.#lo), this.#hi));
    const max = Math.max(min, Math.min(this.#parseInput(this.maxInput, this.#hi), this.#hi));
    this.minRange.value = String(min);
    this.maxRange.value = String(max);
    this.#paint(min, max);
  }

  /** @param {number} min @param {number} max */
  #paint(min, max) {
    const lo = this.#lo;
    const span = this.#hi - lo || 1;
    this.style.setProperty('--pda-fill-start', `${((min - lo) / span) * 100}%`);
    this.style.setProperty('--pda-fill-end', `${((max - lo) / span) * 100}%`);
  }
}

if (!customElements.get('pda-range-slider')) {
  customElements.define('pda-range-slider', PdaRangeSlider);
}

// ── Barre quick-filtres FIXE sous le header au scroll (retour Sam 03/08) ─────
// `.collection-wrapper` est une GRID : un position:sticky sur la barre serait
// borné à sa propre rangée (hauteur = la barre) → no-op. On bascule donc en
// `fixed` (classe .is-stuck) quand la barre atteint le bas du header sticky,
// avec un SPACER qui préserve la rangée de grid (sinon le contenu saute).
function initStickyBar() {
  for (const bar of document.querySelectorAll('.pda-facets-controls')) {
    if (bar.dataset.pdaStickyInit) continue;
    bar.dataset.pdaStickyInit = '1';

    const spacer = document.createElement('div');
    spacer.className = 'pda-facets-controls-spacer';
    spacer.hidden = true;
    bar.after(spacer);

    const headerBottom = () => {
      // --header-height est posé sur BODY (header.js/utilities.js), pas sur <html>
      const v = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
      return Number.isFinite(v) ? v : 80;
    };

    let stuck = false;
    const unstick = () => {
      bar.classList.remove('is-stuck');
      bar.style.left = '';
      bar.style.width = '';
      spacer.hidden = true;
      stuck = false;
    };
    /* ⛔ La barre collée ne passe JAMAIS par-dessus le panneau de filtres. Mesuré le
     * 29/08 : elle recouvrait 76,1 % de son en-tête et rendait la croix de fermeture
     * MORTE au clic (elementFromPoint sur la croix rendait la barre, pas la croix).
     * Elle se borne donc à la colonne de contenu, à droite du panneau. Cote Sam 29/08.
     * ⚠️ Le panneau n'est pas toujours présent (fermé, mobile, page artistes) : sans lui
     * la géométrie d'origine est conservée telle quelle. */
    const bornerAGrille = (left, width) => {
      const panneau = document.querySelector('.facets--vertical');
      if (!panneau) return [left, width];
      const p = panneau.getBoundingClientRect();
      if (!p.width || !p.height) return [left, width];
      /* ⛔ Bord droit lu en géométrie de LAYOUT, pas en rect : le panneau s'ouvre par un
       * keyframe qui le TRANSLATE de −12 px (pda-facets-slide-in). Un rect suit cette
       * translation, donc la barre se calait 12 px trop à gauche pendant 313 ms puis
       * sautait. offsetLeft/offsetWidth ignorent les transforms — le décalage n'existe
       * plus, et il n'y a plus rien à « rattraper » après coup. */
      const socle = panneau.offsetParent || panneau.parentElement;
      const pRight = socle
        ? socle.getBoundingClientRect().left + panneau.offsetLeft + panneau.offsetWidth
        : p.right;
      if (pRight <= left) return [left, width];
      const droite = left + width;
      const bord = Math.min(pRight, droite);
      const restant = droite - bord;
      /* ⛔ Garde : en mobile, « .facets--vertical » EST le drawer et couvre l'écran —
       * borner dessus réduisait la barre à 6,5 px, donc la détruisait. Si le bornage
       * mange plus de la moitié de la barre, c'est qu'on ne borne pas sur une colonne
       * de grille : on renonce et on garde la géométrie d'origine. */
      if (restant < width * 0.5) return [left, width];
      return [bord, restant];
    };
    const setGeom = (left0, width0) => {
      const [left, width] = bornerAGrille(left0, width0);
      // write-if-changed : pas de thrash layout au scroll (le natif throttle en rAF aussi)
      const l = `${left}px`, w = `${width}px`;
      if (bar.style.left !== l) bar.style.left = l;
      if (bar.style.width !== w) bar.style.width = w;
    };
    const update = () => {
      if (!stuck) {
        const rect = bar.getBoundingClientRect();
        if (rect.top <= headerBottom() && rect.width) {
          spacer.style.height = `${rect.height}px`;
          // la barre en fixed sort du flux : sa margin-bottom (rythme grille) part avec
          // elle — le spacer la porte, sinon tout le contenu saute de 32px (revue 03/08)
          spacer.style.marginBottom = getComputedStyle(bar).marginBottom;
          spacer.hidden = false;
          setGeom(rect.left, rect.width);
          bar.classList.add('is-stuck');
          stuck = true;
        }
      } else {
        // morph de section : si le spacer a été détruit, on le remet en place
        if (!spacer.isConnected) bar.after(spacer);
        const srect = spacer.getBoundingClientRect();
        if (!srect.width || srect.top > headerBottom()) {
          unstick();
        } else {
          // resize/état sidebar : recale la géométrie sur celle du spacer
          setGeom(srect.left, srect.width);
        }
      }
    };
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; update(); });
    };
    document.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    // l'ouverture/fermeture de la sidebar change la géométrie de la rangée.
    // ⚠️ pas le parent direct (.shopify-block en display:contents = aucune boîte,
    // l'observer ne tirerait jamais — revue 03/08) : on observe le wrapper de grille.
    /* ⛔ Observer AUSSI le panneau de filtres : sa largeur change quand il s'ouvre ou se
     * ferme, alors que celle du wrapper de grille, elle, ne bouge pas. Mesuré le 29/08 :
     * après fermeture puis réouverture du panneau, la barre gardait 12 px de recouvrement
     * sur l'en-tête, jusqu'au prochain scroll — la géométrie était juste, simplement
     * jamais recalculée. Un observateur qui ne regarde pas ce qui bouge ne prouve rien. */
    const ro = new ResizeObserver(update);
    ro.observe(bar.closest('.collection-wrapper') || document.body);
    const panneauObs = document.querySelector('.facets--vertical');
    if (panneauObs) {
      ro.observe(panneauObs);
      /* ⚠️ Pas d'écoute d'animation ici : elle réparait 313 ms trop tard (le temps du
       * keyframe) et, les événements d'animation BULLANT, elle déclenchait 12 recalculs
       * parasites pour un simple survol de champ. Le décalage est traité à la source,
       * dans bornerAGrille, en lisant une géométrie insensible aux transforms. */
    }
    update();
  }
}
initStickyBar();
// re-render de section (theme editor / morph) : la barre peut être recréée sans son spacer
document.addEventListener('shopify:section:load', () => initStickyBar());
